import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Bell, Package, Plus, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { API_ENDPOINTS } from '@/utils/constants';
import { DashboardData, PaymentAccount, ApiResponse } from '@/services/types';
import { formatCurrency } from '@/utils/formatters';
import { Alert, Button, Card, CountBubble, IconButton, LoadingSkeleton, Overline, SegmentedControl } from '@/components/ui';
import { useOnlineOrderSummary } from '@/features/onlineorders/useOnlineOrderSummary';
import { useUnreadNotifications } from '@/features/notifications/useUnreadNotifications';
import { greetingKey, rangeFor, toLocalDateTimeParams, type RangeKind } from '@/utils/dateRange';
import { CashierCard, HourlyCard } from '@/features/reports/ReportCards';

/** 10 Beranda — KPI hero, orders CTA, stat tiles, rWallet balances, hourly chart + payment mix, top products, staff on duty. */
const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedShop, user } = useAppSelector((state) => state.auth);
  const orders = useOnlineOrderSummary();
  const unread = useUnreadNotifications();
  const [data, setData] = useState<DashboardData | null>(null);
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKind>('today');

  const fetchDashboard = useCallback(async () => {
    if (!selectedShop) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(API_ENDPOINTS.DASHBOARD(selectedShop.shopId), { params: toLocalDateTimeParams(rangeFor(range)) });
      setData(response.data.data);
      // Wallet balances are best-effort; trend, hourly and staff all come from the dashboard payload
      const acc = await api.get<ApiResponse<PaymentAccount[]>>(API_ENDPOINTS.PAYMENT_ACCOUNTS(selectedShop.shopId)).catch(() => null);
      if (acc) setAccounts(acc.data.data);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedShop, range]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (!selectedShop) return null;

  const firstName = user?.fullname?.split(' ')[0] ?? '';
  const avg = data && data.todayTransactions > 0 ? data.todaySales / data.todayTransactions : 0;
  const mix = data ? [
    { label: t('dashboard.cash'), value: data.cashSales, color: 'bg-primary-500' },
    { label: 'QRIS', value: data.qrisSales, color: 'bg-accent-500' },
    { label: 'rWallet', value: data.ewalletSales, color: 'bg-primary-200' },
    { label: t('dashboard.transfer'), value: data.transferSales, color: 'bg-success-500' },
    { label: t('dashboard.card'), value: data.cardSales, color: 'bg-info-500' },
  ].filter((m) => m.value > 0) : [];
  const mixTotal = mix.reduce((n, m) => n + m.value, 0);
  const pending = orders?.newPaidCount ?? 0;
  const walletTotal = accounts.reduce((n, a) => n + a.balance, 0);
  // Server compares the elapsed part of the range with the same stretch one period earlier; null/0 = no trend
  const trend = data && data.previousNetSales != null && data.previousNetSales > 0 ? Math.round(((data.netSales - data.previousNetSales) / data.previousNetSales) * 100) : null;

  const rangeControl = (
    <SegmentedControl fullWidth value={range} onChange={setRange} options={[
      { value: 'today', label: t('dashboard.ranges.today') },
      { value: 'week', label: t('dashboard.ranges.week') },
      { value: 'month', label: t('dashboard.ranges.month') },
    ]} />
  );

  const hero = data && (
    <div className="flex flex-col gap-1.5 rounded-xl bg-surface-brand p-[20px_22px] text-white shadow-brand lg:p-[22px_24px]">
      <div className="flex items-center justify-between gap-2">
        <Overline tone="onBrand">{t('dashboard.netSales')}</Overline>
        {trend !== null && (
          <span className={clsx('flex items-center gap-1 rounded-pill px-2.5 py-0.5 text-xs font-extrabold tnum', trend >= 0 ? 'bg-white/[.14] text-white' : 'bg-status-danger-soft text-danger-700')}>
            {trend >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{trend > 0 ? '+' : ''}{trend}% {t(`dashboard.trendVs.${range}`)}
          </span>
        )}
      </div>
      <span className="text-[36px] font-black leading-[1.1] tnum tracking-[-0.02em] lg:text-[42px]">{formatCurrency(data.netSales)}</span>
      <div className="mt-2.5 flex gap-6 border-t border-white/[.14] pt-3 lg:hidden">
        <span className="flex flex-col"><span className="text-xl font-black tnum">{data.todayTransactions}</span><span className="text-xs text-primary-100">{t('dashboard.transactions').toLowerCase()}</span></span>
        <span className="flex flex-col"><span className="text-xl font-black tnum">{formatCurrency(avg)}</span><span className="text-xs text-primary-100">{t('dashboard.average')}</span></span>
      </div>
      <span className="hidden text-[13px] text-primary-100 lg:block">
        {t('dashboard.gross')} {formatCurrency(data.todaySales)} · {t('dashboard.refunds').toLowerCase()} {formatCurrency(data.todayRefunds)} · {t('dashboard.voids').toLowerCase()} {data.todayVoids}
      </span>
      <span className="text-[13px] text-primary-100">
        {t('report.tax')} +{formatCurrency(data.totalTax || 0)} · {t('report.serviceCharge').toLowerCase()} +{formatCurrency(data.totalServiceCharge || 0)}
      </span>
    </div>
  );

  const ordersCard = (
    <button type="button" onClick={() => navigate('/online-orders')} className={clsx('flex items-center gap-3 rounded-xl bg-surface-card p-4 text-left lg:flex-col lg:items-stretch lg:justify-between lg:p-[22px_24px]', pending > 0 ? 'border-2 border-accent-500' : 'border border-line-subtle shadow-sm')}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-500 text-ink-onAccent lg:hidden"><Package className="h-[21px] w-[21px]" /></span>
      <span className="hidden items-center gap-2 text-[13px] font-extrabold text-ink-muted lg:flex"><Package className="h-[18px] w-[18px] text-accent-700" />{t('onlineOrders.title')}</span>
      <span className="flex min-w-0 flex-1 flex-col lg:flex-none">
        <span className="text-[15px] font-black lg:text-[40px] lg:leading-none lg:tnum">{pending}<span className="lg:hidden"> {t('dashboard.needsAction')}</span></span>
        <span className="text-xs text-ink-muted lg:text-sm lg:font-bold lg:text-ink-secondary"><span className="hidden lg:inline">{t('dashboard.needsAction')} · </span>{t('dashboard.readySent', { ready: orders?.readyCount ?? 0, sent: orders?.sentCount ?? 0 })}</span>
      </span>
      <Button variant="primary" size="sm" className="shrink-0 lg:self-start">{t('dashboard.processNow')}</Button>
    </button>
  );

  const tiles = data && [
    { label: t('dashboard.refunds'), value: formatCurrency(data.todayRefunds) },
    { label: t('dashboard.voids'), value: String(data.todayVoids) },
    { label: t('dashboard.heldCarts'), value: String(data.heldCartsCount), to: '/held-carts' },
  ];

  const mixBody = mixTotal > 0 ? (
    <>
      <div className="flex h-3 gap-[3px] overflow-hidden rounded-pill">
        {mix.map((m) => <span key={m.label} className={m.color} style={{ width: `${(m.value / mixTotal) * 100}%` }} />)}
      </div>
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-xs font-bold text-ink-secondary lg:justify-start lg:gap-x-6">
        {mix.map((m) => <span key={m.label} className="flex items-center gap-1.5"><span className={clsx('h-2 w-2 rounded-full', m.color)} />{m.label} {Math.round((m.value / mixTotal) * 100)}%</span>)}
      </div>
    </>
  ) : <p className="text-sm text-ink-muted">{t('dashboard.noData')}</p>;

  const mixCard = (
    <Card padding="sm" className="flex flex-col gap-3 lg:p-[22px_24px]">
      <span className="text-[15px] font-extrabold lg:text-base">{t('dashboard.paymentBreakdown')}</span>
      {mixBody}
    </Card>
  );

  const topCard = data && (
    <Card padding="sm" className="flex flex-col gap-2.5 lg:p-[20px_22px]">
      <div className="flex items-center justify-between">
        <span className="text-[15px] font-extrabold lg:text-base">{t('dashboard.topProducts')}</span>
        <button type="button" onClick={() => navigate('/reports/daily')} className="text-[13px] font-extrabold text-ink-accent">{t('report.title')}</button>
      </div>
      {data.topProducts.length === 0 ? <p className="text-sm text-ink-muted">{t('dashboard.noData')}</p> : data.topProducts.slice(0, 3).map((p, i) => (
        <div key={p.compositeId} className="flex items-center gap-3">
          <span className={clsx('flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-black tnum', i === 0 ? 'bg-accent-500 text-ink-onAccent' : 'bg-surface-sunken')}>{i + 1}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-bold">{p.name}</span>
          <span className="text-[13px] text-ink-muted tnum">{p.quantitySold}<span className="lg:hidden"> {t('dashboard.sold')}</span></span>
        </div>
      ))}
    </Card>
  );

  const topupLink = (
    <button type="button" onClick={() => navigate('/topup')} className="flex items-center gap-1 text-[13px] font-extrabold text-ink-accent"><Plus className="h-4 w-4" />{t('topup.title')}</button>
  );
  const noAccounts = <p className="text-sm text-ink-muted">{t('checkout.noPaymentAccounts')}</p>;

  // Phone: horizontal carousel of account cards, full-bleed so the scroller reaches the screen edge
  const walletStrip = (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="text-[15px] font-extrabold">rWallet{accounts.length > 0 && <span className="text-[13px] font-bold text-ink-muted tnum"> · {formatCurrency(walletTotal)}</span>}</span>
        {topupLink}
      </div>
      {accounts.length === 0 ? noAccounts : (
        <div className="-mx-5 flex gap-2.5 overflow-x-auto px-5 pb-0.5 [scrollbar-width:none]">
          {accounts.map((a) => (
            <div key={a.id} className={clsx('flex w-[150px] shrink-0 flex-col gap-0.5 rounded-lg bg-surface-card p-[10px_12px]', a.isMainAccount ? 'border-2 border-primary-500' : 'border border-line-subtle')}>
              <span className={clsx('flex items-center gap-1.5 truncate text-xs font-extrabold', a.isMainAccount ? 'text-ink-brand' : 'text-ink-secondary')}><Wallet className="h-3.5 w-3.5 shrink-0" />{a.accountName}</span>
              <span className="text-base font-black tnum">{formatCurrency(a.balance)}</span>
              <span className="truncate text-[11px] text-ink-muted">{a.isMainAccount && `${t('common.active')} · `}{a.accountNumber}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Landscape: total block + one row per account
  const walletCard = (
    <Card padding="sm" className="flex min-h-0 flex-col gap-3 lg:p-[20px_22px]">
      <div className="flex items-center justify-between">
        <span className="text-base font-extrabold">rWallet</span>
        {topupLink}
      </div>
      {accounts.length === 0 ? noAccounts : (
        <>
          <div className="flex flex-col gap-0.5 rounded-md bg-brandsoft p-[12px_14px]">
            <span className="text-xs font-extrabold text-ink-muted">{t('dashboard.totalBalance', { count: accounts.length })}</span>
            <span className="text-2xl font-black tracking-[-0.01em] tnum text-ink-brand">{formatCurrency(walletTotal)}</span>
          </div>
          <div className="flex flex-col">
            {accounts.map((a) => (
              <div key={a.id} className="flex items-center gap-3 border-t border-line-subtle py-[9px] first:border-t-0">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brandsoft text-ink-brand"><Wallet className="h-[17px] w-[17px]" /></span>
                <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-bold">{a.accountName}</span><span className="truncate text-xs text-ink-muted tnum">{a.accountNumber}{a.isMainAccount && ` · ${t('common.active')}`}</span></span>
                <span className="text-sm font-extrabold tnum">{formatCurrency(a.balance)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col px-5 pt-2.5 lg:px-8 lg:pt-7">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-2xl font-black tracking-[-0.02em] lg:text-[30px]">{t(`dashboard.greeting.${greetingKey()}`, { name: firstName })}</h1>
          <p className="truncate text-[13px] text-ink-muted lg:text-sm">{selectedShop.shopName} · {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
        </div>
        <div className="hidden w-[320px] lg:block">{rangeControl}</div>
        <IconButton variant="card" label={t('notifications.title')} onClick={() => navigate('/notifications')}>
          <Bell />
          <CountBubble count={unread} tone="danger" className="absolute -top-0.5 -right-0.5" />
        </IconButton>
      </div>
      <div className="mt-3 lg:hidden">{rangeControl}</div>

      <div className="mt-3.5 min-h-0 flex-1 overflow-y-auto pb-6 lg:mt-5">
        {error && <Alert variant="error" message={error} className="mb-3" />}
        {isLoading && !data ? (
          <div className="flex flex-col gap-3"><LoadingSkeleton height={140} /><LoadingSkeleton height={72} /><LoadingSkeleton height={120} /></div>
        ) : data && (
          <div className={clsx('flex flex-col gap-3', isLoading && 'opacity-60')}>
            {/* Phone stack */}
            <div className="flex flex-col gap-3 lg:hidden">
              {hero}
              {ordersCard}
              <div className="grid grid-cols-3 gap-2.5">
                {tiles!.map((x) => (
                  <Card key={x.label} padding="none" elevation="flat" className={clsx('flex flex-col gap-0.5 p-3', x.to && 'cursor-pointer')} onClick={x.to ? () => navigate(x.to!) : undefined}>
                    <span className="text-xs font-bold text-ink-muted">{x.label}</span>
                    <span className="text-[17px] font-black tnum">{x.value}</span>
                  </Card>
                ))}
              </div>
              {walletStrip}
              {mixCard}
              {topCard}
            </div>
            {/* Landscape grid */}
            <div className="hidden flex-col gap-4 lg:flex">
              <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-4">
                {hero}
                <Card className="flex flex-col justify-between lg:p-[22px_24px]">
                  <span className="text-[13px] font-extrabold text-ink-muted">{t('dashboard.transactions')}</span>
                  <span className="text-[40px] font-black leading-none tnum">{data.todayTransactions}</span>
                  <span className="text-[13px] text-ink-muted">{t('dashboard.perTransaction', { amount: formatCurrency(avg) })}</span>
                </Card>
                {ordersCard}
              </div>
              <div className="grid min-h-0 flex-1 grid-cols-[1.15fr_1fr_1fr] items-start gap-4">
                <HourlyCard data={{ salesByHour: data.salesByHour ?? [] }}>{mixBody}</HourlyCard>
                <div className="flex flex-col gap-4">{topCard}<CashierCard data={{ salesByCashier: data.salesByCashier ?? [] }} compact title={t('dashboard.staffOnDuty')} /></div>
                {walletCard}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
